/**
 * EngineReviewPanel — 12-engine grid with attest workflow and FinalizeModal
 *
 * Phase 4.5 — redesign-p4-analacct-45b
 * Renders the 12-engine grid, detail view, and FinalizeModal CP 04 11 gate.
 * Reuses existing engine review infrastructure from AccountantEngineReviewLanding.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  CheckCircle2,
  Clock,
  Play,
  Loader2,
  AlertTriangle,
  ChevronRight,
  FileCheck,
  ArrowRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/utils/financialFormat';
import { BRAND_NAME } from '@/config/brand';

// Import UI primitives
import { ToolTile } from '@/components/ui/ToolTile';
import { DataTable, Column } from '@/components/ui/DataTable';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { MetricCard } from '@/components/shared/MetricCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';

// Import FinalizeModal
import { FinalizeModal } from '@/components/accountant/review';

// Import engine review types and API
import {
  ENGINE_NAMES,
  ENGINE_DESCRIPTIONS,
  type EngineId,
  type EngineReviewListResponse,
  type EngineReviewState,
  type ReviewStateDisplay,
  REVIEW_STATE_DISPLAY,
} from '@/types/engineReview';
import {
  getEngineReviewList,
  runInitialEngines,
} from '@/services/engineReviewApi';

// Import report sections from AccountantReportView
import {
  AccountantKeyMetrics,
  AdjusterAnalysisSection,
  AttorneyAnalysisSection,
  FairSettlementSection,
} from '@/components/accountant';

export interface EngineReviewPanelProps {
  reportId: string;
  reportData: any;
  canonicalNet?: number | null;
}

const ENGINE_ORDER: EngineId[] = [
  'merchant_reconciliation',
  'merchant_revenue_augmentation',
  'bottom_up',
  'enhanced_claim',
  'dual_method',
  'mitigation',
  'impact',
  'epi',
  'policy_deductions',
  'pv',
  'fair_settlement',
  'case_strength',
  'property',
];

// Map engine review states to StatusBadge variants
const stateToBadgeVariant: Record<EngineReviewState, any> = {
  unreviewed: 'not_started',
  under_review: 'active',
  approved: 'done',
  disputed: 'attention',
  stale_after_rerun: 'warning',
  rerun_pending: 'pending',
  waived: 'neutral',
};

function EngineCard({
  engineId,
  status,
  overridesCount,
  lastRunAt,
  onClick,
}: {
  engineId: EngineId;
  status: EngineReviewState;
  overridesCount: number;
  lastRunAt?: string;
  onClick: () => void;
}) {
  const display = REVIEW_STATE_DISPLAY[status];
  const badgeVariant = stateToBadgeVariant[status];

  return (
    <div
      onClick={onClick}
      className={cn(
        'group cursor-pointer rounded-[13px] border border-[var(--color-border)]',
        'bg-[var(--color-bg-surface)] p-5 transition-all',
        'hover:border-[var(--color-brand)] hover:bg-[var(--color-bg-elevated)]'
      )}
      data-testid={`engine-card-${engineId}`}
    >
      <div className="flex items-start justify-between">
        <h3
          className="text-sm font-semibold text-[var(--color-text-primary)]"
          style={{ fontFamily: 'Outfit, system-ui, sans-serif' }}
        >
          {ENGINE_NAMES[engineId]}
        </h3>
        <StatusBadge text={display.label} variant={badgeVariant} size="sm" />
      </div>

      <p className="mt-2 text-xs leading-relaxed text-[var(--color-text-muted)]">
        {ENGINE_DESCRIPTIONS[engineId]}
      </p>

      <div className="mt-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {overridesCount > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-[var(--color-brand)]">
              <AlertTriangle className="h-3 w-3" />
              {overridesCount} override{overridesCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {lastRunAt && (
          <span className="text-xs text-[var(--color-text-dim)]">
            {new Date(lastRunAt).toLocaleDateString()}
          </span>
        )}
      </div>

      <div className="mt-3 flex items-center gap-1 text-xs font-medium text-[var(--color-brand)] opacity-0 transition-opacity group-hover:opacity-100">
        Review
        <ChevronRight className="h-3 w-3" />
      </div>
    </div>
  );
}

export function EngineReviewPanel({ reportId, reportData, canonicalNet }: EngineReviewPanelProps) {
  const navigate = useNavigate();
  const [data, setData] = useState<EngineReviewListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runFeedback, setRunFeedback] = useState<
    | { kind: 'success'; dispatchId: string; status: string; autoApproved: boolean }
    | { kind: 'error'; message: string }
    | null
  >(null);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalized, setFinalized] = useState(false);

  // Fetch engine data
  useEffect(() => {
    if (!reportId) {
      setLoading(false);
      return;
    }
    fetchEngines();
  }, [reportId]);

  // Cooldown timer
  useEffect(() => {
    if (!cooldownUntil) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [cooldownUntil]);

  const fetchEngines = async () => {
    setLoading(true);
    try {
      const result = await getEngineReviewList(reportId);
      setData(result);
    } catch (err: any) {
      // 404 = no reviews started yet, render empty grid
      setData({
        report_id: reportId,
        engines: [],
        all_approved: false,
        finalize_enabled: false,
        attestable_total: ENGINE_ORDER.length - 1, // Excluding property
        attestable_approved: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const cooldownRemaining =
    cooldownUntil && cooldownUntil > now ? Math.ceil((cooldownUntil - now) / 1000) : 0;
  const engines = data?.engines ?? [];
  const anyUnderReview = engines.some((e) => e.status === 'under_review');
  const runDisabled = running || cooldownRemaining > 0 || anyUnderReview;

  const handleRunInitial = async () => {
    if (!reportId || runDisabled) return;
    setRunning(true);
    setRunFeedback(null);
    try {
      const res = await runInitialEngines(reportId);
      setRunFeedback({
        kind: 'success',
        dispatchId: res.dispatch_id,
        status: res.status,
        autoApproved: res.auto_approved,
      });
      setCooldownUntil(Date.now() + 60_000);
      setTimeout(() => fetchEngines(), 5_000);
    } catch (err: any) {
      setRunFeedback({
        kind: 'error',
        message: err?.message || 'Failed to queue engine pipeline.',
      });
    } finally {
      setRunning(false);
    }
  };

  const handleEngineClick = (engineId: EngineId) => {
    navigate(`/dashboard/accountant/review/${reportId}/${engineId}`);
  };

  const result = reportData.result;

  return (
    <div className="space-y-6">
      {/* Run Feedback Banner */}
      {runFeedback && (
        <div
          className={cn(
            'rounded-md border px-4 py-3 text-sm',
            runFeedback.kind === 'success'
              ? 'border-[rgba(34,197,94,0.30)] bg-[rgba(34,197,94,0.12)] text-[#22C55E]'
              : 'border-[rgba(255,102,0,0.30)] bg-[rgba(255,102,0,0.12)] text-[var(--color-brand)]'
          )}
        >
          {runFeedback.kind === 'success' ? (
            <>
              {runFeedback.autoApproved
                ? 'Engine pipeline queued. Outputs will appear within ~1–3 minutes.'
                : `Engine pipeline dispatch created — ${runFeedback.status}. Approve on the dispatch queue dashboard to start the run.`}
              {` (dispatch: ${runFeedback.dispatchId})`}
            </>
          ) : (
            <>Failed to queue engine pipeline: {runFeedback.message}</>
          )}
        </div>
      )}

      {/* Key Metrics Section */}
      {result && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            label={canonicalNet != null ? 'Report snapshot (pre-deduction)' : 'Final Claim Amount'}
            value={<FinancialValue value={result.final_claim_amount} size={canonicalNet != null ? 'md' : 'lg'} />}
            accent={canonicalNet == null}
            subtitle={canonicalNet != null ? 'Before policy deductions — see Net supported claim above' : undefined}
            testId="report-snapshot-metric"
          />
          <MetricCard
            label="Variable Profit Loss"
            value={<FinancialValue value={result.step1_variable_profit_loss} />}
          />
          <MetricCard
            label="Incremental Loss"
            value={<FinancialValue value={result.step2_incremental_loss} />}
          />
          <MetricCard
            label="Engines Approved"
            value={`${data?.attestable_approved || 0} / ${data?.attestable_total || 0}`}
            badge={
              data?.all_approved
                ? { text: 'All Approved', variant: 'done' }
                : { text: 'In Progress', variant: 'active' }
            }
          />
        </div>
      )}

      {/* Engine Grid */}
      <div className="rounded-[13px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
        <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2
              className="text-lg font-semibold text-[var(--color-text-primary)]"
              style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
            >
              Engine Review & Attest
            </h2>
            <p className="text-sm text-[var(--color-text-muted)]">
              Review and attest to each calculation engine's output
            </p>
          </div>

          <div className="flex items-center gap-3">
            {data?.finalize_enabled && !finalized && (
              <Button
                onClick={() => setFinalizeOpen(true)}
                data-testid="open-finalize-modal"
                className="bg-[var(--color-brand)] text-white hover:bg-[var(--color-brand-hover)]"
              >
                <Shield className="mr-2 h-4 w-4" />
                Finalize Attestation
              </Button>
            )}
            {finalized && (
              <StatusBadge text="Attested" variant="done" size="md" />
            )}
            <Button
              onClick={handleRunInitial}
              disabled={runDisabled}
              variant="outline"
              data-testid="run-initial-engines"
            >
              {running ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Queueing…
                </>
              ) : cooldownRemaining > 0 ? (
                <>
                  <Clock className="mr-2 h-4 w-4" />
                  Cooldown ({cooldownRemaining}s)
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Run Engines
                </>
              )}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {ENGINE_ORDER.map((id) => (
              <div
                key={id}
                className="h-32 animate-pulse rounded-[13px] bg-[var(--color-bg-elevated)]"
              />
            ))}
          </div>
        ) : engines.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>No engine outputs to review yet</p>
        ) : (
          <div
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            data-testid="engine-card-grid"
          >
            {ENGINE_ORDER.map((engineId) => {
              const engine = engines.find((e) => e.engine_id === engineId);
              const status = engine?.status ?? 'unreviewed';

              return (
                <EngineCard
                  key={engineId}
                  engineId={engineId}
                  status={status}
                  overridesCount={engine?.overrides_count || 0}
                  lastRunAt={engine?.last_run_at}
                  onClick={() => handleEngineClick(engineId)}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Forensic AI Analysis Sections */}
      <div className="space-y-6">
        {reportData && (
          <AdjusterAnalysisSection
            reportId={reportId}
            getAuthHeader={() => ({})} // Auth handled by api utility
            formatCurrency={formatCurrency}
          />
        )}

        {reportData && (
          <AttorneyAnalysisSection
            reportId={reportId}
            getAuthHeader={() => ({})}
            formatCurrency={formatCurrency}
          />
        )}

        {reportData && (
          <FairSettlementSection
            reportId={reportId}
            getAuthHeader={() => ({})}
            formatCurrency={formatCurrency}
          />
        )}
      </div>

      {/* FinalizeModal with CP 04 11 Gate */}
      <FinalizeModal
        isOpen={finalizeOpen}
        onClose={() => setFinalizeOpen(false)}
        reportId={reportId}
        onFinalized={() => {
          setFinalized(true);
          setFinalizeOpen(false);
        }}
      />
    </div>
  );
}

export default EngineReviewPanel;
